/**
 * The browser side of PENNY realtime voice: microphone → 16 kHz PCM over a WebSocket, and PENNY's ordered
 * audio parts played back in order. It holds no key and decides nothing: the server owns the session state,
 * the conversation, the tools and every fact. This file only moves sound and shows what the server says.
 */

const API: string = (import.meta.env?.VITE_API_BASE_URL as string | undefined) ?? '/api/v1';
const DEVICE_KEY = 'jt.penny.device';

/** A guest device id (the API's guest rule), kept in this browser. Random, never derived from anything. */
export function deviceId(
  storage: Pick<Storage, 'getItem' | 'setItem'> | undefined = globalThis.localStorage,
): string {
  try {
    const known = storage?.getItem(DEVICE_KEY);
    if (known !== null && known !== undefined && /^[A-Za-z0-9_-]{8,100}$/u.test(known)) return known;
  } catch {
    // Storage blocked (private window): a fresh id for this page.
  }
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  const id = `web-${Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')}`;
  try {
    storage?.setItem(DEVICE_KEY, id);
  } catch {
    // Not kept: acceptable.
  }
  return id;
}

/** Float samples at `fromRate` → 16-bit little-endian mono at 16 kHz (linear interpolation). */
export function toPcm16k(input: Float32Array, fromRate: number): Uint8Array {
  const ratio = fromRate / 16_000;
  const length = Math.floor(input.length / ratio);
  const out = new Int16Array(length);
  for (let i = 0; i < length; i += 1) {
    const at = i * ratio;
    const k = Math.floor(at);
    const a = input[k] ?? 0;
    const b = input[k + 1] ?? a;
    const v = Math.max(-1, Math.min(1, a + (b - a) * (at - k)));
    out[i] = v < 0 ? v * 0x8000 : v * 0x7fff;
  }
  return new Uint8Array(out.buffer);
}

export function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) out[i] = bin.charCodeAt(i);
  return out;
}

export type ServerEvent = { readonly type: string } & Record<string, unknown>;

/** Opens (or reopens) a voice session: conversation + ticket through the ordinary API, then the socket. */
export async function openVoiceSession(options: {
  readonly device: string;
  readonly conversationId?: string;
  readonly voiceSessionId?: string;
  /** A voice key from GET /consumer/penny/voices (new sessions only). */
  readonly voice?: string;
  /** 'en', 'auto' or an ISO 639-1 code (new sessions only). */
  readonly language?: string;
}): Promise<{ conversationId: string; voiceSessionId: string; socket: WebSocket }> {
  const headers = { 'content-type': 'application/json', 'x-jt-device': options.device };
  let conversationId = options.conversationId;
  if (conversationId === undefined) {
    const res = await fetch(`${API}/consumer/penny/conversations`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ channel: 'WEB' }),
    });
    if (!res.ok) throw new Error(`PENNY is not available (${res.status}).`);
    conversationId = ((await res.json()) as { data: { conversationId: string } }).data.conversationId;
  }
  const t = await fetch(`${API}/consumer/penny/conversations/${conversationId}/realtime`, {
    method: 'POST',
    headers,
    body: JSON.stringify(
      options.voiceSessionId !== undefined
        ? { voiceSessionId: options.voiceSessionId }
        : {
            ...(options.voice !== undefined ? { voice: options.voice } : {}),
            ...(options.language !== undefined ? { language: options.language } : {}),
          },
    ),
  });
  if (!t.ok) throw new Error(`Voice is not available (${t.status}).`);
  const ticket = ((await t.json()) as { data: { voiceSessionId: string; ticket: string; path: string } })
    .data;
  const scheme = location.protocol === 'https:' ? 'wss' : 'ws';
  const socket = new WebSocket(
    `${scheme}://${location.host}${ticket.path}?ticket=${encodeURIComponent(ticket.ticket)}`,
  );
  socket.binaryType = 'arraybuffer';
  return { conversationId, voiceSessionId: ticket.voiceSessionId, socket };
}

/**
 * Plays one generation's parts in order, gaplessly, and stops dead on `stop()`. Parts of any other
 * generation are ignored; a stopped generation can never play again.
 */
const JITTER_S = 0.12;

export class OrderedPlayer {
  private generation: number | undefined;
  private readonly stale = new Set<number>();
  private sources: AudioBufferSourceNode[] = [];
  private nextAt = 0;
  private chain: Promise<void> = Promise.resolve();
  private pending = 0;
  private ended = false;
  /** Parts that arrived (or finished decoding) for a stopped generation and were NOT played. Must stay 0 in practice. */
  staleParts = 0;

  constructor(
    private readonly ctx: AudioContext,
    private readonly onFinished: (generation: number) => void,
  ) {}

  /** Whether PENNY is audibly playing right now. */
  get playing(): boolean {
    return this.sources.length > 0;
  }

  /**
   * One part of `generation`. MP3 is decoded; raw PCM (`audio/pcm;rate=N`, streamed as Fish generates it) is
   * played directly — the first chunk of a reply waits a small jitter buffer, the rest follow back to back.
   */
  part(generation: number, bytes: Uint8Array, mediaType = 'audio/mpeg'): void {
    if (this.stale.has(generation)) {
      this.staleParts += 1;
      return;
    }
    if (this.generation !== generation) {
      if (this.generation !== undefined) this.stop(this.generation);
      this.generation = generation;
      this.nextAt = 0;
      this.ended = false;
    }
    this.pending += 1;
    // Decode in arrival order (parts arrive in speaking order), so scheduling stays in order.
    this.chain = this.chain.then(async () => {
      try {
        const pcm = /^audio\/pcm;rate=(\d+)/u.exec(mediaType);
        const buffer =
          pcm !== null
            ? this.pcmBuffer(bytes, Number(pcm[1]))
            : await this.ctx.decodeAudioData(bytes.slice().buffer);
        if (this.generation !== generation || this.stale.has(generation)) {
          this.staleParts += 1;
          return;
        }
        const source = this.ctx.createBufferSource();
        source.buffer = buffer;
        source.connect(this.ctx.destination);
        // First audio of a reply — or the first after the queue ran dry (PENNY speaks sentence by sentence while
        // she is still writing): a 120 ms cushion, so network jitter does not break the speech into pieces.
        const dry = this.nextAt === 0 || this.nextAt < this.ctx.currentTime;
        const at = dry ? this.ctx.currentTime + JITTER_S : this.nextAt;
        source.start(at);
        this.nextAt = at + buffer.duration;
        this.sources.push(source);
        source.onended = () => {
          this.sources = this.sources.filter((s) => s !== source);
          this.maybeFinished(generation);
        };
      } finally {
        this.pending -= 1;
      }
    });
  }

  /** The server sent every part of `generation`. */
  end(generation: number): void {
    if (this.generation !== generation) return;
    this.ended = true;
    void this.chain.then(() => this.maybeFinished(generation));
  }

  private pcmBuffer(bytes: Uint8Array, rate: number): AudioBuffer {
    const samples = new Int16Array(bytes.slice().buffer);
    const buffer = this.ctx.createBuffer(1, Math.max(1, samples.length), rate);
    const channel = buffer.getChannelData(0);
    for (let i = 0; i < samples.length; i += 1) channel[i] = (samples[i] as number) / 32_768;
    return buffer;
  }

  /** Stops `generation` for good; returns how many audio sources were actually cut off. */
  stop(generation: number): number {
    this.stale.add(generation);
    if (this.generation !== generation) return 0;
    const cut = this.sources.length;
    for (const s of this.sources) {
      try {
        s.stop();
      } catch {
        // Already stopped.
      }
    }
    this.sources = [];
    this.generation = undefined;
    return cut;
  }

  private maybeFinished(generation: number): void {
    if (this.generation === generation && this.ended && this.pending === 0 && this.sources.length === 0) {
      this.generation = undefined;
      this.onFinished(generation);
    }
  }
}

export interface VoiceChoice {
  readonly key: string;
  readonly label: string;
  readonly gender: string;
  readonly accent: string;
}

export async function listVoices(): Promise<readonly VoiceChoice[]> {
  let r: Response;
  try {
    r = await fetch(`${API}/consumer/penny/voices`);
  } catch {
    // No answer at all (offline, or no page origin to resolve against): the default voice, as for an error.
    return [];
  }
  if (!r.ok) return [];
  return ((await r.json()) as { data: { voices: VoiceChoice[] } }).data.voices;
}

export function voicePreviewUrl(key: string): string {
  return `${API}/consumer/penny/voices/${encodeURIComponent(key)}/preview`;
}

/**
 * Languages Whisper can hear (its published list), named by the browser. Speech quality varies by
 * language (Fish speaks ~80 of them well); the server checks every choice again.
 */
const WHISPER_LANGUAGES =
  'af am ar as az ba be bg bn bo br bs ca cs cy da de el es et eu fa fi fo fr gl gu ha haw he hi hr ht hu hy id is it ja ka kk km kn ko la lb ln lo lt lv mg mi mk ml mn mr ms mt my ne nl nn no oc pa pl ps pt ro ru sa sd si sk sl sn so sq sr su sv sw ta te tg th tk tl tr tt uk ur uz vi yi yo yue zh'.split(
    ' ',
  );

export function languageChoices(): readonly { readonly code: string; readonly name: string }[] {
  let names: Intl.DisplayNames | undefined;
  try {
    names = new Intl.DisplayNames(['en'], { type: 'language' });
  } catch {
    names = undefined;
  }
  return WHISPER_LANGUAGES.map((code) => ({ code, name: names?.of(code) ?? code }))
    .filter((l) => l.name !== l.code)
    .sort((a, b) => a.name.localeCompare(b.name));
}
